/**
 * Derive a compact human-readable label for a citation URN emitted by the
 * BIM Comply v2 SSE stream. The v2 event schema (see FindingEvent.cs) does
 * NOT ship the resolver's `title` field for wire-size reasons, so the Web
 * Plugin reconstructs a short label from the URN so citations remain
 * recognisable inside the DockablePane host without a round-trip.
 *
 * Supported URN shape:
 * `legis://<COUNTRY>/<DOC>/<YEAR>#(sec|table)-<REFERENCE>`.
 *   e.g. `legis://IE/TGD-K/2014#sec-1.1.4` → `TGD K §1.1.4`.
 *
 * URNs that do not match this shape return the raw URN so nothing is lost
 * from the UI when a new legislation family is introduced upstream.
 */

const URN_PATTERN = /^legis:\/\/(?<country>[^/]+)\/(?<doc>[^/]+)\/(?<year>[^#]+)#(?<anchorType>sec|table)-(?<reference>.+)$/;

export interface ParsedCitationUrn {
  country: string;
  doc: string;
  year: string;
  anchorType: 'section' | 'table';
  reference: string;
}

export function citationReferenceLabel(parsed: ParsedCitationUrn): string {
  if (parsed.anchorType === 'section') {
    return `§${parsed.reference}`;
  }

  const [tableNumber, ...descriptionParts] = parsed.reference.split('-');
  const description = descriptionParts
    .map((part) => part ? `${part[0].toUpperCase()}${part.slice(1)}` : '')
    .filter(Boolean)
    .join(' ');
  return `Table ${tableNumber}${description ? ` - ${description}` : ''}`;
}

export function parseCitationUrn(urn: string): ParsedCitationUrn | null {
  if (!urn) {
    return null;
  }

  const match = URN_PATTERN.exec(urn);
  if (!match || !match.groups) {
    return null;
  }

  return {
    country: match.groups.country,
    doc: match.groups.doc,
    year: match.groups.year,
    anchorType: match.groups.anchorType === 'table' ? 'table' : 'section',
    reference: match.groups.reference,
  };
}

/**
 * Human-readable label for a citation URN, e.g. `TGD K §1.1.4`. Falls back
 * to the raw URN when the URN does not follow the known `legis://` shape.
 */
export function citationLabel(urn: string): string {
  const parsed = parseCitationUrn(urn);
  if (!parsed) {
    return urn;
  }

  const doc = parsed.doc.replace(/-/g, ' ');
  return `${doc} ${citationReferenceLabel(parsed)}`;
}

/**
 * Longer accessible name for a citation URN so screen readers announce the
 * in-app detail action even though only the compact label is painted.
 */
export function citationAccessibleName(urn: string): string {
  const parsed = parseCitationUrn(urn);
  if (!parsed) {
    return urn;
  }

  const doc = parsed.doc.replace(/-/g, ' ');
  return `View citation details for ${doc} ${citationReferenceLabel(parsed)} (${parsed.country}, ${parsed.year})`;
}
